import { STORAGE_ERROR_CODE } from "./constants"
import { deserialize, notifyError, serialize, validateKey } from "./utils"

import { WebApp } from "../../constants"

import type { ResolvedStorageError, StorageOperationCallbacks } from "./utils"

const cloudStorage = WebApp.CloudStorage

/**
 * Wrapper around `WebApp.CloudStorage` that owns the local snapshot, the
 * subscriber registry, and the TG bridge for every storage operation.
 *
 * Manual verification runbook: `docs/runbooks/telegram-cloud-storage-smoke.js`.
 * Run sections of it in a real Mini App's DevTools console when the TG
 * contract changes (callback shapes, error codes, payload formats) — TG
 * contract branches in this file trace back to sections in that file.
 */
export class CloudStorageController {
  private listeners = new Map<string, Set<() => void>>()
  private resetListeners = new Set<() => void>()
  private storage = new Map<string, unknown>()
  private deserializationFailures = new Set<string>()
  private resetRevision = 0

  /**
   * Read a single key. Cache-first: if the controller has already settled
   * this key (success OR missing/null), return the cached value synchronously
   * without hitting TG. Deserialization failures are deliberately not settled
   * so a later hook bootstrap can repair corrupt cloud JSON.
   *
   * On a cache miss the snapshot is updated and `emit(key)` always fires,
   * regardless of resolved value. This keeps the invariant literal:
   * **mutating storage Map → notifying subscribers**. React still skips
   * spurious rerenders because `getSnapshot` returns `null` for both
   * "absent" and "null", and useSyncExternalStore uses Object.is.
   *
   * `callbacks.onSuccess` fires on every settled read (cache hit OR fresh
   * fetch), so consumers can't tell the two paths apart. `callbacks.onError`
   * fires only when the operation is unsuccessful and the read returns null.
   */
  async getItem<T>(key: string, callbacks?: StorageOperationCallbacks): Promise<T | null> {
    const keyError = validateKey(key)
    if (keyError !== null) {
      notifyError("Error reading from cloud storage:", keyError, callbacks)
      return null
    }

    if (this.storage.has(key) && !this.deserializationFailures.has(key)) {
      callbacks?.onSuccess?.()
      return this.storage.get(key) as T | null
    }

    const operationRevision = this.resetRevision
    let rawValue: string | undefined
    try {
      rawValue = await new Promise<string | undefined>((resolve, reject) => {
        cloudStorage.getItem(key, (error, value) => {
          if (error !== null) reject(new Error(error))
          else resolve(value)
        })
      })
    } catch (error) {
      if (this.isStaleOperation(operationRevision)) return null
      notifyError("Error reading from cloud storage:", error, callbacks)
      return null
    }
    if (this.isStaleOperation(operationRevision)) return null

    let response: T | null = null
    let deserializationError: unknown = null
    if (rawValue) {
      try {
        response = deserialize(rawValue) as T
      } catch (error) {
        // Don't silently swallow — broken JSON in the cloud is a real failure
        // mode that previously left the cache poisoned with `null` AND the
        // consumer unaware. Surface via the same notifyError path as every
        // other error so the toast fires and the constants table's
        // DESERIALIZATION_ERROR code is actually reachable.
        deserializationError = error
        response = null
      }
    }

    this.storage.set(key, response)
    if (deserializationError !== null) {
      this.deserializationFailures.add(key)
    } else {
      this.deserializationFailures.delete(key)
    }
    this.emit(key)
    if (deserializationError !== null) {
      notifyError("Error reading from cloud storage:", deserializationError, callbacks)
    } else {
      callbacks?.onSuccess?.()
    }
    return response
  }

  async setItem(key: string, value: unknown, callbacks?: StorageOperationCallbacks): Promise<void> {
    const keyError = validateKey(key)
    if (keyError !== null) {
      notifyError("Error saving to cloud storage:", keyError, callbacks)
      return
    }

    let serialized: string
    try {
      serialized = serialize(value)
    } catch (error) {
      notifyError("Error saving to cloud storage:", error, callbacks)
      return
    }

    if (serialized.length > 4096) {
      notifyError("Error saving to cloud storage:", STORAGE_ERROR_CODE.DATA_TOO_LONG, callbacks)
      return
    }

    const operationRevision = this.resetRevision
    try {
      await new Promise<void>((resolve, reject) => {
        cloudStorage.setItem(key, serialized, (error, success) => {
          if (error !== null) reject(new Error(error))
          else if (success) resolve()
          else reject(new Error(STORAGE_ERROR_CODE.SAVE_FAILED))
        })
      })
    } catch (error) {
      if (this.isStaleOperation(operationRevision)) return
      notifyError("Error saving to cloud storage:", error, callbacks)
      return
    }
    if (this.isStaleOperation(operationRevision)) return

    // Cache the round-tripped value rather than the raw input: JSON.stringify
    // silently drops `undefined`, functions, and symbols, so the snapshot would
    // drift from what TG actually stored if we cached `value` as-is. Since
    // `serialized` came from `serialize(value)` above, deserialize is guaranteed
    // to succeed — no try/catch needed.
    this.storage.set(key, deserialize(serialized))
    this.deserializationFailures.delete(key)
    this.emit(key)
    callbacks?.onSuccess?.()
  }

  async deleteItem(key: string, callbacks?: StorageOperationCallbacks): Promise<void> {
    const keyError = validateKey(key)
    if (keyError !== null) {
      notifyError("Error deleting from cloud storage:", keyError, callbacks)
      return
    }

    const operationRevision = this.resetRevision
    try {
      await new Promise<void>((resolve, reject) => {
        cloudStorage.removeItem(key, (error, success) => {
          if (error !== null) reject(new Error(error))
          else if (success) resolve()
          else reject(new Error(STORAGE_ERROR_CODE.DELETE_FAILED))
        })
      })
    } catch (error) {
      if (this.isStaleOperation(operationRevision)) return
      notifyError("Error deleting from cloud storage:", error, callbacks)
      return
    }
    if (this.isStaleOperation(operationRevision)) return

    this.storage.set(key, null)
    this.deserializationFailures.delete(key)
    this.emit(key)
    callbacks?.onSuccess?.()
  }

  /**
   * Bulk read — wraps TG's getItems. Cache-first: any key already settled in
   * the snapshot (from a prior get/set/getItems) is served from memory; only
   * the missing subset goes to TG in a single round-trip. If every requested
   * key is already cached, the network call is skipped entirely.
   *
   * For non-empty requests, TG failures, stale reset completions, unexpected
   * payloads, and key-validation errors resolve to every requested key mapped
   * to `null`. Cached values are NOT leaked into that error result; the
   * existing cache stays intact and can still be read via getSnapshot for any
   * caller that wants partial-tolerant reads.
   *
   * For every key actually fetched (i.e. previously missing) the snapshot
   * is updated and `emit(key)` always fires — same invariant as getItem,
   * applied per key in the missing subset.
   *
   * TG returns a Record<key, value> for non-empty input (missing keys are
   * included with an empty string, not omitted); the empty-input array
   * branch from the API never reaches this code because we short-circuit
   * above.
   *
   * `callbacks.onSuccess` fires when the call resolves to a usable result
   * (including the empty-input short-circuit and full cache-hit path).
   * `callbacks.onError` fires on validation failures, TG errors, and
   * deserialization failures for fetched values.
   */
  async getItems<T = unknown>(
    keys: readonly string[],
    callbacks?: StorageOperationCallbacks
  ): Promise<Record<string, T | null>> {
    const targetKeys = [...keys]

    if (targetKeys.length === 0) {
      callbacks?.onSuccess?.()
      return {}
    }

    // Error-path return shape: every requested key mapped to null. Keeps the
    // success-shape contract intact (real TG always returns a full record per
    // smoke verification, so consumers can iterate uniformly).
    const allNulls = (): Record<string, T | null> =>
      Object.fromEntries(targetKeys.map((key): [string, T | null] => [key, null]))

    for (const key of targetKeys) {
      const keyError = validateKey(key)
      if (keyError !== null) {
        notifyError("Error reading from cloud storage:", keyError, callbacks)
        return allNulls()
      }
    }

    const result: Record<string, T | null> = {}
    const setResult = (key: string, value: T | null) => {
      Object.defineProperty(result, key, {
        value,
        enumerable: true,
        configurable: true,
        writable: true
      })
    }
    const missing: string[] = []
    for (const key of targetKeys) {
      if (this.storage.has(key) && !this.deserializationFailures.has(key)) {
        setResult(key, this.storage.get(key) as T | null)
      } else {
        missing.push(key)
      }
    }

    if (missing.length === 0) {
      callbacks?.onSuccess?.()
      return result
    }

    const operationRevision = this.resetRevision
    let rawValues: Record<string, unknown>
    try {
      rawValues = await new Promise<Record<string, unknown>>((resolve, reject) => {
        cloudStorage.getItems(missing, (error, values) => {
          if (error !== null) {
            reject(new Error(error))
            return
          }
          if (!values) {
            reject(new Error(STORAGE_ERROR_CODE.UNEXPECTED_GETITEMS_PAYLOAD))
            return
          }
          // TG returns [] only for empty input (we short-circuit above). Any
          // array for a non-empty request means the payload shape drifted.
          if (Array.isArray(values)) {
            reject(new Error(STORAGE_ERROR_CODE.UNEXPECTED_GETITEMS_PAYLOAD))
            return
          }
          resolve(values)
        })
      })
    } catch (error) {
      if (this.isStaleOperation(operationRevision)) return allNulls()
      notifyError("Error reading from cloud storage:", error, callbacks)
      return allNulls()
    }
    if (this.isStaleOperation(operationRevision)) return allNulls()

    if (missing.some(key => !Object.hasOwn(rawValues, key) || typeof rawValues[key] !== "string")) {
      notifyError("Error reading from cloud storage:", STORAGE_ERROR_CODE.UNEXPECTED_GETITEMS_PAYLOAD, callbacks)
      return allNulls()
    }

    // Collect deserialization failures across all missing keys; surface as a
    // single onError call after the loop (one batch op → one outcome). Each
    // bad key still snapshots to null + emits so subscribers can render their
    // fallback, but it remains uninitialized so a later bootstrap can repair it.
    let firstDeserializationError: unknown = null
    for (const key of missing) {
      const rawValue = rawValues[key] as string
      let response: T | null = null
      let hasDeserializationError = false
      if (rawValue) {
        try {
          response = deserialize(rawValue) as T
        } catch (error) {
          if (firstDeserializationError === null) firstDeserializationError = error
          hasDeserializationError = true
          response = null
        }
      }
      setResult(key, response)
      this.storage.set(key, response)
      if (hasDeserializationError) {
        this.deserializationFailures.add(key)
      } else {
        this.deserializationFailures.delete(key)
      }
      this.emit(key)
    }
    if (firstDeserializationError !== null) {
      notifyError("Error reading from cloud storage:", firstDeserializationError, callbacks)
    } else {
      callbacks?.onSuccess?.()
    }
    return result
  }

  /**
   * Bulk write — TG has no native setItems endpoint, so this composes parallel
   * `setItem` calls (best-effort, not fail-fast). All writes are issued; the
   * outer `onError` fires once with the FIRST failure to settle (which is
   * **not** guaranteed to be the first in iteration order — TG callback
   * timing decides). Other failures are still logged by their inner `setItem`
   * calls. On a partial failure the snapshot reflects whichever writes
   * individually resolved successfully — there is no rollback.
   *
   * Validation short-circuits the whole batch: a single invalid key aborts
   * before any TG call so partial-state surprise is minimized for the most
   * common failure mode.
   */
  async setItems(entries: Record<string, unknown>, callbacks?: StorageOperationCallbacks): Promise<void> {
    const keys = Object.keys(entries)
    if (keys.length === 0) {
      callbacks?.onSuccess?.()
      return
    }

    for (const key of keys) {
      const keyError = validateKey(key)
      if (keyError !== null) {
        notifyError("Error saving to cloud storage:", keyError, callbacks)
        return
      }
    }

    const operationRevision = this.resetRevision
    let firstError: ResolvedStorageError | undefined
    await Promise.all(
      keys.map(key =>
        this.setItem(key, entries[key], {
          onError: error => {
            if (!firstError) firstError = error
          }
        })
      )
    )
    if (this.isStaleOperation(operationRevision)) return
    if (firstError) {
      callbacks?.onError?.(firstError)
      return
    }
    callbacks?.onSuccess?.()
  }

  /**
   * Bulk delete — wraps TG's removeItems. Single round-trip, all-or-nothing:
   * if TG returns success=false the whole batch is treated as failed and
   * the snapshot stays unchanged. Successful deletions settle each key to
   * `null` so mounted hooks do not re-bootstrap and silently recreate the
   * default value. Invalid keys short-circuit before the network call, same
   * as deleteItem.
   */
  async deleteItems(keys: string[], callbacks?: StorageOperationCallbacks): Promise<void> {
    const targetKeys = [...keys]
    if (targetKeys.length === 0) {
      callbacks?.onSuccess?.()
      return
    }

    for (const key of targetKeys) {
      const keyError = validateKey(key)
      if (keyError !== null) {
        notifyError("Error deleting from cloud storage:", keyError, callbacks)
        return
      }
    }

    const operationRevision = this.resetRevision
    try {
      await new Promise<void>((resolve, reject) => {
        cloudStorage.removeItems(targetKeys, (error, success) => {
          if (error !== null) reject(new Error(error))
          else if (success) resolve()
          else reject(new Error(STORAGE_ERROR_CODE.DELETE_FAILED))
        })
      })
    } catch (error) {
      if (this.isStaleOperation(operationRevision)) return
      notifyError("Error deleting from cloud storage:", error, callbacks)
      return
    }
    if (this.isStaleOperation(operationRevision)) return

    for (const key of targetKeys) {
      this.storage.set(key, null)
      this.deserializationFailures.delete(key)
      this.emit(key)
    }
    callbacks?.onSuccess?.()
  }

  /**
   * Returns every key the user has stored in TG CloudStorage, or `null` on
   * any failure (TG error, callback resolving without a payload =
   * LIST_KEYS_FAILED). `[]` means storage is genuinely empty — the two
   * states are distinguishable. Failures are also logged via logStorageError
   * and surfaced through `callbacks.onError`.
   */
  async getKeys(callbacks?: StorageOperationCallbacks): Promise<string[] | null> {
    const operationRevision = this.resetRevision
    let keys: string[]
    try {
      keys = await new Promise<string[]>((resolve, reject) => {
        cloudStorage.getKeys((error, value) => {
          if (error !== null) reject(new Error(error))
          else if (Array.isArray(value) && value.every(key => typeof key === "string")) resolve(value)
          else reject(new Error(STORAGE_ERROR_CODE.LIST_KEYS_FAILED))
        })
      })
    } catch (error) {
      if (this.isStaleOperation(operationRevision)) return null
      notifyError("Error listing keys from cloud storage:", error, callbacks)
      return null
    }
    if (this.isStaleOperation(operationRevision)) return null
    callbacks?.onSuccess?.()
    return keys
  }

  /**
   * Removes every key the user has in TG CloudStorage. Composed from getKeys
   * + deleteItems — TG does not expose a native CloudStorage.clear endpoint.
   * Inherits deleteItems semantics: each cleared key is settled to `null`
   * and emits to its subscribers. A no-op when storage is empty (or getKeys
   * fails — that path is already logged by getKeys).
   *
   * Caller's `callbacks.onError` propagates whichever inner step failed —
   * we DON'T re-log it (inner getKeys/deleteItems already did so under their
   * own prefix); we only forward the resolved error to the consumer. Caller's
   * `callbacks.onSuccess` fires once at the end of a clean wipe (including
   * the empty-storage short-circuit).
   */
  async clear(callbacks?: StorageOperationCallbacks): Promise<void> {
    const operationRevision = this.resetRevision
    const keys = await this.getKeys({
      onError: callbacks?.onError
    })
    if (this.isStaleOperation(operationRevision)) {
      return
    }
    if (keys === null) {
      return
    }
    if (keys.length === 0) {
      callbacks?.onSuccess?.()
      return
    }
    await this.deleteItems(keys, callbacks)
  }

  /**
   * Clears the local snapshot without touching TG.
   * Two legitimate uses:
   *  - tests, between fixtures (so each test starts from a clean cache);
   *  - runtime, on user sign-out — drop another user's PII from the cache
   *    before a different account signs in.
   *
   * Does NOT remove anything from TG — call `clear()` for that.
   */
  reset(): void {
    const affectedKeys = new Set(this.storage.keys())
    this.storage.clear()
    this.deserializationFailures.clear()
    this.resetRevision += 1
    for (const key of affectedKeys) this.emit(key)
    this.resetListeners.forEach(listener => {
      listener()
    })
  }

  /**
   * Read-only view of the subscriber registry, keyed by storage key. Useful
   * for tests (assert cleanup after unmount / key-change) and runtime
   * diagnostics (debugging unexpected re-renders). `.get(key)` returns
   * `undefined` when nobody is subscribed — Map types this honestly.
   */
  get subscribers(): ReadonlyMap<string, ReadonlySet<() => void>> {
    return this.listeners
  }

  subscribe(key: string, listener: () => void): () => void {
    let set = this.listeners.get(key)
    if (!set) {
      set = new Set()
      this.listeners.set(key, set)
    }
    set.add(listener)

    return () => {
      // Idempotent: a previous cleanup call may have already dropped the
      // empty Set entry below, so guard before touching it.
      const current = this.listeners.get(key)
      if (!current) return
      current.delete(listener)
      // Drop the empty Set so the registry doesn't grow unboundedly with
      // dead entries for keys nobody re-subscribes to (e.g. one-shot modals).
      if (current.size === 0) this.listeners.delete(key)
    }
  }

  subscribeToReset(listener: () => void): () => void {
    this.resetListeners.add(listener)

    return () => {
      this.resetListeners.delete(listener)
    }
  }

  getSnapshot(key: string) {
    return this.storage.get(key) ?? null
  }

  getResetSnapshot(): number {
    return this.resetRevision
  }

  isInitialized(key: string): boolean {
    return this.storage.has(key) && !this.deserializationFailures.has(key)
  }

  private emit(key: string): void {
    this.listeners.get(key)?.forEach(listener => {
      listener()
    })
  }

  private isStaleOperation(operationRevision: number): boolean {
    return operationRevision !== this.resetRevision
  }
}

export const cloudStorageController = new CloudStorageController()
